import { StarFilled } from '@ant-design/icons';
import { useMemo } from 'react';
import { useTranslate } from '@tolgee/react';
import { starColors } from 'views/Conversation/ConvList/starData';
import { useOrgConfigStateValue } from '../useOrgConfig';
import { useAuthorization } from 'components/Authorization';
import { useGetPermission } from '../useNewPermisson';
import { useQuery } from '@tanstack/react-query';
import { queries } from 'services/queries';

export type ConversationFilterAdvance = Omit<
  ApiConversation.ConversationListAdvanceParams,
  'pageSize'
>;

// 筛选器的模式
export type ConversationFilterAdvanceMode = 'single' | 'multiple';

export const useFilterOptions = () => {
  const { t } = useTranslate();

  const startFilter = useMemo(
    () =>
      [
        {
          name: t('filter.RED'),
          value: 'RED',
          icon: <StarFilled className="mr-1" style={{ color: starColors.RED }} />,
        },
        {
          name: t('filter.YELLOW'),
          value: 'YELLOW',
          icon: <StarFilled className="mr-1" style={{ color: starColors.YELLOW }} />,
        },
        {
          name: t('filter.BLUE'),
          value: 'BLUE',
          icon: <StarFilled className="mr-1" style={{ color: starColors.BLUE }} />,
        },
        {
          name: t('filter.GREEN'),
          value: 'GREEN',
          icon: <StarFilled className="mr-1" style={{ color: starColors.GREEN }} />,
        },
        {
          name: t('filter.PURPLE'),
          value: 'PURPLE',
          icon: <StarFilled className="mr-1" style={{ color: starColors.PURPLE }} />,
        },
      ] as const,
    [t],
  );

  const toBeCheckedFilter = useMemo(
    () => [
      {
        name: t('conv_filter_advance_wait_reply'),
        value: 'UNPROCESSED',
      },
      {
        name: t('to_be_checked'),
        value: 'BOT_REPLIED',
      },
    ],
    [t],
  );

  return {
    startFilter,
    toBeCheckedFilter,
  };
};

// 是否有待检查选项
export const useFilterToBeCheckedEnable = () => {
  const { qaEnable, whiteListConfig: { UNPROCESSED_WITH_BOT_REPLIED = false, LLM = false } = {} } =
    useOrgConfigStateValue();

  const { data: { enableAI = false } = {} } = useQuery(queries.accounts.orgConfig);

  const enable = useMemo(
    () => (qaEnable || (LLM && enableAI)) && !UNPROCESSED_WITH_BOT_REPLIED,
    [qaEnable, UNPROCESSED_WITH_BOT_REPLIED, LLM, enableAI],
  );

  return enable;
};

/** 与列表/工具栏一致的数据源：分类视图应传入该分类下的 filterAdvance / mode，否则名称会与全局 atom 错位 */
export type UseFilterNameOptions = {
  filterAdvance?: ConversationFilterAdvance;
  mode?: ConversationFilterAdvanceMode;
};

export const useFilterName = (options: UseFilterNameOptions) => {
  const { mode, filterAdvance } = options;
  const { t } = useTranslate();
  const toBeCheckedEnable = useFilterToBeCheckedEnable();
  const { whiteListConfig: { SIMPLUS_CONV_STATUS = false } = {} } = useOrgConfigStateValue();

  return useMemo(() => {
    let filterName = '';

    const mapper: any = {
      IN_RECEPTION: t('inreception'),
      UNREAD: t('filter.unread'),
      WAIT_ASSIGN: t('filter.unassigned'),
      BEFORE_SALES_STATUS: t('aide.售前'),
      DURING_SALES_STATUS: t('aide.售中'),
      AFTER_SALES_STATUS: t('aide.售后'),
      UNPROCESSED: t('filter.replied'),
      BOT_REPLIED: SIMPLUS_CONV_STATUS ? t('to_be_checked') : t('filter.replied'),
      REPUTATION: t('影响声誉'),
      MEDIATION: t('调解中'),
      MANUAL_CLOSE: t('人工关闭'),
      NOT_CLOSE: t('未关闭'),
      IMPORTANT_TODO: t('重要待办'),
    };
    if (mode === 'multiple') {
      let count = 0;
      const plus = (data?: any[]) => {
        if (data?.length) {
          count += 1;
        }
      };
      plus(filterAdvance?.statusFilters);
      plus(filterAdvance?.salesStatusFilters);
      plus(filterAdvance?.asteriskFilters);
      plus(filterAdvance?.assistantFilters);
      if (filterAdvance?.excludeShopeeAffiliate || filterAdvance?.onlyShopeeAffiliate) {
        count += 1;
      }
      filterName = t('feedback.共x项', { x: count });
    }
    if (mode === 'single') {
      if (filterAdvance?.statusFilters?.length) {
        if (
          filterAdvance.statusFilters[0] === 'UNPROCESSED' &&
          filterAdvance.statusFilters[1] === 'NOT_CLOSE'
        ) {
          filterName = t('inreception');
        } else {
          const [statusFilter] = filterAdvance.statusFilters;
          if (statusFilter === 'BOT_REPLIED' && toBeCheckedEnable) {
            filterName = t('to_be_checked');
          } else {
            filterName = mapper[statusFilter];
          }
        }
      }
      if (filterAdvance?.asteriskFilters?.length) {
        filterName = t('filter.asterisk');
      }
      if (filterAdvance?.salesStatusFilters?.length) {
        const [salesStatusFilter] = filterAdvance.salesStatusFilters;
        filterName = mapper[salesStatusFilter];
      }
      if (filterAdvance?.assistantFilters?.length) {
        filterName = t('department.客服');
      }
      if (filterAdvance?.onlyShopeeAffiliate) {
        filterName = `Shopee ${t('达人会话')}`;
      }
      if (filterAdvance?.excludeShopeeAffiliate) {
        filterName = `Shopee ${t('买家会话')}`;
      }
      if (filterAdvance?.existKnowledgePopup) {
        filterName = t('filter.qapair');
      }
    }

    return filterName;
  }, [mode, filterAdvance, toBeCheckedEnable, t, SIMPLUS_CONV_STATUS]);
};

export const useManualCloseEnable = () => {
  const hasSuperviser = useGetPermission()('管理员视角');
  const { payload } = useAuthorization<Oversea.Token.Payload>();

  if (!hasSuperviser) {
    return false;
  }

  if (import.meta.env.VITE_ENV === 'staging' || import.meta.env.VITE_ENV === 'feature') {
    return payload?.orgId === 1163;
  }

  return payload?.orgId === 2159;
};
